import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, TextField } from '@material-ui/core'
import SydModal from '../../../../components/commonDesign/SydModal'
import SydModalActions from '../../../../components/commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../components/commonDesign/Button'
import SydSelect from '../../../../components/commonDesign/SydSelect'
import SydLabel from '../../../../components/commonDesign/SydLabel'
import Text from '../../../../components/atoms/Text'
import { useListAiFeedbackMetrics } from '../../../../api/denaliAssistant'

const useStyles = makeStyles(() => ({
  textField: {
    width: '100%',
    marginBottom: 'var(--margin-md)',
    '& .MuiInputBase-root': {
      fontSize: 'var(--font-size-sm)'
    }
  },
  selectWrap: {
    width: '100%',
    marginBottom: 'var(--margin-md)'
  },
  errorText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-danger-500)',
    marginBottom: 'var(--margin-sm)'
  }
}))

function MessageFeedbackModal ({ open, onClose, feedbackType, onSubmit }) {
  const classes = useStyles()
  const [feedbackText, setFeedbackText] = useState('')
  const [feedbackMetricId, setFeedbackMetricId] = useState(null)
  const selectRef = useRef(null)

  const metricSentiment = feedbackType === 'thumbs_up' ? 'POSITIVE' : 'NEGATIVE'
  const { data: metrics = [], isLoading, isError } = useListAiFeedbackMetrics(metricSentiment, {
    enabled: open
  })

  const selectOptions = useMemo(
    () => (metrics || []).map((m) => ({
      value: m.metricId,
      label: m.metricDisplay
    })),
    [metrics]
  )

  const placeholder = feedbackType === 'thumbs_up'
    ? 'Tell us what you liked...'
    : 'Tell us what you didn\'t like...'

  const canSubmit =
    !isLoading &&
    !isError &&
    selectOptions.length > 0 &&
    feedbackMetricId != null

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return
    if (onSubmit) {
      onSubmit({ feedbackMetricId, feedbackText: feedbackText.trim() })
    }
    setFeedbackText('')
    setFeedbackMetricId(null)
    onClose()
  }, [canSubmit, feedbackMetricId, feedbackText, onSubmit, onClose])

  const handleCancel = useCallback(() => {
    setFeedbackText('')
    setFeedbackMetricId(null)
    onClose()
  }, [onClose])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSubmit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }, [handleSubmit, handleCancel])

  useEffect(() => {
    if (!open) {
      setFeedbackText('')
      setFeedbackMetricId(null)
      return
    }
    selectRef.current?.focus?.()
  }, [open])

  return (
    <SydModal
      open={open}
      onClose={handleCancel}
      title='Feedback'
      size='small'
      position='middle'
    >
      <div className={classes.selectWrap}>
        <SydLabel label='Why are you giving this feedback?' required>
          <SydSelect
            ref={selectRef}
            name='feedbackMetricId'
            minWidth='0'
            placeholder={isLoading ? 'Loading…' : 'Select a reason'}
            options={selectOptions}
            value={feedbackMetricId}
            onChange={(e) => setFeedbackMetricId(e.target.value)}
            disabled={isLoading || isError || selectOptions.length === 0}
          />
        </SydLabel>
      </div>
      {isError && (
        <Text className={classes.errorText}>
          Couldn&apos;t load feedback reasons. Try again later or contact support.
        </Text>
      )}
      {!isLoading && !isError && selectOptions.length === 0 && (
        <Text className={classes.errorText}>
          No feedback reasons configured for this thread. Contact your administrator.
        </Text>
      )}
      <SydLabel label='Additional details (optional)'>
        <TextField
          className={classes.textField}
          multiline
          minRows={2}
          placeholder={placeholder}
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          onKeyDown={handleKeyDown}
          variant='outlined'
        />
      </SydLabel>
      <SydModalActions>
        <SydButton
          onClick={handleCancel}
          variant='outline'
        >
          Cancel
        </SydButton>
        <SydButton
          onClick={handleSubmit}
          variant='primary'
          disabled={!canSubmit}
          endIcon={feedbackType === 'thumbs_up' ? 'thumbUp' : 'thumbDown'}
        >
          Send
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

MessageFeedbackModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  feedbackType: PropTypes.oneOf(['thumbs_up', 'thumbs_down']).isRequired,
  onSubmit: PropTypes.func.isRequired
}

export default MessageFeedbackModal
